// The visitor's own position on the map -- the blue dot -- reduced to the parts
// that are decidable without a map.
//
// Pure on purpose, like labels.js: what arrives here is a browser callback at an
// awkward moment (a second position fix landing mid-pan) or a
// GeolocationPositionError carrying a numeric code and nothing else. Both are
// rules, not rendering, and both are things we would otherwise only ever find
// out about by standing outside with a phone.
//
// Note what is deliberately NOT here: a storage key. The labels toggle remembers
// itself across visits; this one must not. A remembered "tracking was on" is a
// claim about a past visit, and acting on it would start a watch -- and so fire
// a permission prompt -- at a visitor who has asked for nothing on this one, on
// a partner's site, in an iframe they never opted into.
//
// The browser already keeps the only record that is safe to act on, and it
// answers without prompting: `navigator.permissions.query({name:
// 'geolocation'})` reports 'granted' | 'prompt' | 'denied'. So a watch may
// begin two ways and no others -- a press, or a 'granted' that this visitor
// gave this origin themselves (`shouldAutoLocate`). Which of the two started it
// is not bookkeeping: it decides whether the map is allowed to move
// (`shouldRecenter`) and whether a failure is worth reporting
// (`shouldReportLocateError`), because only a press asked a question.

/**
 * Options passed verbatim to `map.locate()`.
 *
 * Leaflet merges these over its own defaults (`timeout: 10000, watch: false`)
 * and hands the merged object straight to `navigator.geolocation.watchPosition`
 * as its PositionOptions -- verified in leaflet 1.9.4, `Map.locate`. So every
 * key below is an instruction to the device's radio, not a Leaflet preference.
 *
 * - `watch: true` -- the dot has to follow the visitor as they walk toward a
 *   piece; a single fix would freeze them at the corner where they pressed the
 *   button. It is also what makes teardown something to be explicit about:
 *   `watchPosition` registers with the browser, so the watch outlives any
 *   component state that forgets about it. Leaflet's own `map.remove()` does
 *   end one (`if (this._locationWatchId !== undefined) { this.stopLocate(); }`,
 *   leaflet-src.js:3874), but that only helps on the path where the map is
 *   destroyed in the same breath; every other exit has to call
 *   `map.stopLocate()` itself.
 *
 * - `setView: false` -- Leaflet's own recentering runs on EVERY response
 *   (`Map._handleGeolocationResponse` calls `this.setView` whenever the option
 *   is set), which under `watch: true` means the map yanks itself back to the
 *   visitor once per GPS update, including while they are dragging it. The
 *   first-fix-only rule below replaces it; see `shouldRecenter`.
 *
 * - `enableHighAccuracy: true` -- the coarse wifi/cell fix is routinely a few
 *   hundred metres wide, which in a city puts the dot on the wrong block. This
 *   map exists to walk someone to a mural, so the wrong block is the wrong
 *   answer. The cost is real and worth stating: high accuracy under `watch:
 *   true` is the expensive pair on a phone, because it keeps the GPS radio
 *   awake. That cost is bounded by the two hard stops (the toggle, and
 *   `beforeDestroy`) rather than by asking for a worse fix.
 *
 * - `maximumAge: 10000` -- Leaflet's documented default is 0, meaning every
 *   callback must be a freshly acquired fix. Accepting one up to ten seconds
 *   old lets the platform answer from its last reading instead of waking the
 *   chip again. At walking pace (~1.4 m/s) a ten-second-old fix is ~14 m stale,
 *   comfortably inside the accuracy circle already drawn around the dot, so the
 *   staleness is never larger than the uncertainty we are honestly showing.
 *
 * - `timeout: 15000` -- Leaflet's default is 10000, which a cold GPS chip
 *   indoors or between tall buildings misses regularly; the visitor then gets
 *   "took too long" while the device is still working on it. Under `watch` this
 *   timeout applies per acquisition, and a timeout is treated as transient
 *   (see `isFatalLocateError`), so a slow first fix costs a message, not the
 *   watch.
 *
 * Frozen because `map.locate()` keeps the merged object as `this._locateOptions`
 * and reads it again on every callback: a caller mutating what it passed in
 * would be editing the behaviour of a watch that is already running.
 */
export const LOCATE_OPTIONS = Object.freeze({
  watch: true,
  setView: false,
  enableHighAccuracy: true,
  maximumAge: 10000,
  timeout: 15000,
})

/**
 * How a tracking session began. Passed around rather than inferred, because
 * nothing about a `locationfound` event says who asked for it.
 *
 * Frozen and compared by value: `shouldRecenter` and `shouldReportLocateError`
 * both branch on it, and both are written so that a value neither of these
 * names is a value that proves nothing.
 */
export const LOCATE_TRIGGER = Object.freeze({
  // A finger on the "My location" button. A question, and one owed an answer
  // on screen even when the answer is "no".
  PRESS: 'press',
  // Started by the map itself on an already-granted permission. Asked nothing,
  // so it is owed nothing: no recentre, no error message.
  AUTO: 'auto',
})

/**
 * Whether the map may start tracking without anyone pressing anything, given
 * the state `navigator.permissions.query({name: 'geolocation'})` reported.
 *
 * Only `'granted'`, and the reason is the one hard safety property of this
 * feature: MapComponent is also the embedded map on partner sites
 * (views/EmbedMap.vue, packs/map_widget.js), where nothing has ever asked the
 * visitor for anything. `'granted'` is a decision this visitor already made for
 * this origin, so acting on it raises no dialog and asks no new question.
 * `'prompt'` would raise one -- at someone who merely loaded a third party's
 * page -- and `'denied'` would buy a watch that can only fail.
 *
 * The permission query itself is what makes this askable at all: per the
 * Permissions API it reports the state without triggering the prompt that
 * `getCurrentPosition`/`watchPosition` would.
 *
 * @param {string} permissionState `PermissionStatus.state`.
 * @returns {boolean}
 */
export function shouldAutoLocate(permissionState) {
  // An allowlist of exactly one, deliberately, rather than `!== 'denied'`.
  // Everything that is not a proven grant -- a Permissions API that is not
  // there, a query that rejected, a state some later revision of the spec adds
  // -- has to land on "do not start", and only equality gives that for free.
  return permissionState === 'granted'
}

/**
 * Whether a `locationfound` event should move the map.
 *
 * Recentring is a property of how tracking STARTED, not of the fix count alone.
 * A press asked "where am I", and the dot is useless if it lands off-screen, so
 * the first fix of a press moves the map. A watch that started on its own asked
 * nothing: the dot simply appears wherever the visitor is, off-screen included,
 * because a map that jumps to your position while you are reading about a mural
 * three neighbourhoods away has answered a question nobody put.
 *
 * The fix count then bounds even the press to ONE move. Every fix after the
 * first arrives on the browser's schedule rather than the visitor's, and a map
 * that recentres once per GPS update is a map you cannot pan.
 *
 * @param {number} fixCount Fixes already handled since tracking started.
 * @param {string} trigger One of `LOCATE_TRIGGER`.
 * @returns {boolean}
 */
export function shouldRecenter(fixCount, trigger) {
  // Both halves are proofs, not defaults. `PRESS` by equality so an absent or
  // unrecognised trigger cannot pass as one; the count strictly `=== 0`,
  // because `''`, `null` and `false` are all `== 0` and a caller whose counter
  // went missing has not shown this is the first fix. Between the two failure
  // modes -- the dot drawn off-screen once, or the map jumping under a finger
  // mid-gesture -- only the first is recoverable, because the visitor can
  // press again.
  return trigger === LOCATE_TRIGGER.PRESS && fixCount === 0
}

/**
 * Whether a `locationerror` should reach the button: its title, its error
 * colour, and the live region that announces it.
 *
 * A watch the visitor never started should not shout. Nobody is waiting for
 * this answer, and "Location blocked" on a button that was never pressed
 * reports a failure the visitor did not cause -- on a partner's page they were
 * only reading. The error is still acted on where it counts: `isFatalLocateError`
 * still ends the watch, so an auto session that loses permission mid-visit
 * stops rather than sitting there holding the browser's location indicator
 * open. It just stops quietly, and the button falls back to "Show my location",
 * which is true.
 *
 * Note this defaults the OPPOSITE way to `shouldRecenter`: anything that is not
 * a proven AUTO reports. The two failure modes are not symmetric. A message
 * shown to someone who did not ask is confusing; silence towards someone who
 * did ask leaves them waiting for a dot that is never coming, and the live
 * region is the only channel a screen reader has for that (WCAG 4.1.3).
 *
 * @param {string} trigger One of `LOCATE_TRIGGER`.
 * @returns {boolean}
 */
export function shouldReportLocateError(trigger) {
  return trigger !== LOCATE_TRIGGER.AUTO
}

// Codes as defined by the W3C Geolocation API's GeolocationPositionError, which
// Leaflet copies onto its `locationerror` event as `e.code` untouched.
//
// 0 is Leaflet's own invention and not a W3C code: `Map.locate` synthesises
// `{code: 0, message: 'Geolocation not supported.'}` when `'geolocation' in
// navigator` is false (verified in leaflet 1.9.4). It has to be handled here
// or it would fall through to the generic message and read like something the
// visitor did wrong.
//
// The strings are written for the button's `title`, which is the only place
// they surface: short, and specific about who can act. Leaflet's own
// `e.message` is deliberately unused -- it is prose for a console ("Geolocation
// error: permission denied."), prefixed and punctuated by Leaflet itself.
const MESSAGES = {
  0: 'This browser cannot share your location.',
  1: 'Location blocked. Allow it in your browser settings to see where you are.',
  2: 'Your location is unavailable right now.',
  3: 'Finding your location is taking too long.',
}

const UNKNOWN_MESSAGE = 'Your location could not be found.'

/**
 * A human sentence for a `locationerror` code.
 *
 * Always returns a string, including for a code we have never seen or none at
 * all. The caller assigns the result to `button.title`, and assigning
 * `undefined` there renders the literal text "undefined" to anyone who hovers.
 *
 * @param {number} code `e.code` from Leaflet's `locationerror` event.
 * @returns {string}
 */
export function locateErrorMessage(code) {
  // Property lookup would coerce '1' to the same key as 1 and quietly claim the
  // browser reported a permission denial; anything that is not a number is a
  // shape we do not understand, so it gets the honest generic sentence.
  if (typeof code !== 'number') return UNKNOWN_MESSAGE
  return MESSAGES[code] || UNKNOWN_MESSAGE
}

// The errors a running watch can still recover from on a later callback of the
// same watch: the phone that walks out of a tunnel (2), the cold chip that
// missed one acquisition window (3). Tearing the watch down for these would
// turn a two-second glitch into a button that has to be pressed again.
const TRANSIENT_CODES = [2, 3]

/**
 * Whether an error should end tracking outright.
 *
 * Permission denied (1) is fatal because the watch survives it and will never
 * produce a fix: the browser does not re-prompt, so what remains is a
 * registered watch, a permission indicator lit in the browser chrome, and no
 * dot -- for the rest of the visit. Leaflet's code 0 is fatal for the plainer
 * reason that the API is not there.
 *
 * Anything unrecognised is fatal too. We cannot prove an unfamiliar failure is
 * self-correcting, and an unbounded watch reporting an error nobody understands
 * is exactly the leak the teardown rules exist to prevent. Stopping is
 * recoverable by pressing the button; not stopping is not.
 *
 * @param {number} code `e.code` from Leaflet's `locationerror` event.
 * @returns {boolean} True when the caller should `stopLocate()` and reset.
 */
export function isFatalLocateError(code) {
  return !TRANSIENT_CODES.includes(code)
}

export default LOCATE_OPTIONS
